import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import siteUrl from "../../const/const";
import { cn } from "@/lib/utils";

export interface RekvizitData { text: boolean; rekvizit: string }
export interface QuestionValue { id: number; text: string; answer: string; rekvizit: RekvizitData | null }
export interface QuestionViewQuestion {
  values: QuestionValue[];
  isRekvizitForAll: boolean;
  notebefore: string | null;
  considered: string | null;
  comment: string | null;
  sources: string[];
  authors: { id: number; fullname: string }[];
}

/** Response of GET questions/{id}/view. */
export interface QuestionViewData {
  question: QuestionViewQuestion;
  packageId: number;
  packageName: string;
  phaseName: string;
  gameName: string;
}

const TextLines: React.FC<{ text: string; indent?: boolean }> = ({ text, indent }) =>
  <>
    {text.split("/-/").map((m, i) => (
      <span key={i}>
        {indent && i ? <span>&nbsp;&nbsp;</span> : null}
        {m}
        <br />
      </span>
    ))}
  </>;

/** A single question with a reveal toggle for its answer block (as on /questions/:id). */
const QuestionView: React.FC<{ question: QuestionViewQuestion }> = ({ question: q }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMulti = q.values.length > 1;

  const renderTypeLabel = () => {
    switch (q.values.length) {
      case 2: return <span className="font-bold text-blue-500 dark:text-indigo-300">Duplet</span>;
      case 3: return <span className="font-bold text-blue-500 dark:text-indigo-300">Blits</span>;
      case 4: return <span className="font-bold text-blue-500 dark:text-indigo-300">Kvadriplet</span>;
      default: return null;
    }
  };

  return (
    <>
      {isMulti && (
        <p className="mb-2 text-base">{renderTypeLabel()}</p>
      )}

      {q.isRekvizitForAll && q.values[0].rekvizit && (
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Paylama materialı</p>
          {q.values[0].rekvizit.text ? (
            <p className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
              <TextLines text={q.values[0].rekvizit.rekvizit} />
            </p>
          ) : (
            <img
              src={`${siteUrl}images/${q.values[0].rekvizit.rekvizit}`}
              alt=""
              className="max-w-full max-h-48 rounded-lg"
            />
          )}
        </div>
      )}

      {q.notebefore && (
        <p className="text-gray-800 dark:text-gray-100 font-semibold mb-1">{q.notebefore}</p>
      )}

      <div className="space-y-1">
        {q.values.map((qv, i) => {
          const hasRek = qv.rekvizit !== null;
          const rekText = hasRek ? qv.rekvizit!.text : false;

          return (
            <div key={i} className="text-gray-800 dark:text-gray-100 font-semibold break-words">
              {isMulti ? (
                hasRek && !q.isRekvizitForAll ? (
                  <p>{i + 1}. Rekvizit</p>
                ) : (
                  <p>{i + 1}. <TextLines text={qv.text} indent /></p>
                )
              ) : (
                !q.notebefore ? (
                  hasRek ? (
                    <p><span className="font-bold">Paylama materialı</span></p>
                  ) : (
                    <p><TextLines text={qv.text} indent /></p>
                  )
                ) : (
                  hasRek ? (
                    <p>Paylama materialı</p>
                  ) : (
                    <p><TextLines text={qv.text} /></p>
                  )
                )
              )}

              {rekText && !q.isRekvizitForAll && (
                <p className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm mt-1">
                  <TextLines text={qv.rekvizit!.rekvizit} />
                </p>
              )}
              {hasRek && !q.isRekvizitForAll && !rekText && (
                <img
                  src={`${siteUrl}images/${qv.rekvizit!.rekvizit}`}
                  alt=""
                  className="max-w-full max-h-40 rounded-lg mt-1"
                />
              )}
              {hasRek && !q.isRekvizitForAll && (
                <p className="text-gray-800 dark:text-gray-100 font-semibold">
                  <TextLines text={qv.text} />
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Reveal toggle */}
      <button
        onClick={() => setIsOpen(v => !v)}
        className="flex items-center gap-1 mt-3 text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
        aria-label="Cavabı göstər / gizlət"
      >
        <ChevronRight
          className={cn(
            "w-4 h-4 transition-transform duration-300",
            isOpen && "rotate-90"
          )}
        />
        {isOpen ? "Cavabı gizlət" : "Cavabı göstər"}
      </button>

      {/* Answer block */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300",
          isOpen ? "max-h-[2000px] opacity-100 mt-3" : "max-h-0 opacity-0"
        )}
      >
        <div className="space-y-1.5 text-sm border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-gray-700 dark:text-gray-200 font-medium">
            <span className="font-bold text-gray-900 dark:text-gray-50">Cavab: </span>
            {q.values.length > 1
              ? `1. ${q.values[0].answer}`
              : q.values[0].answer}
            {q.values[1] ? ` 2. ${q.values[1].answer}` : null}
            {q.values[2] ? ` 3. ${q.values[2].answer}` : null}
            {q.values[3] ? ` 4. ${q.values[3].answer}` : null}
          </p>
          {q.considered && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">Sayılma meyarı: </span>
              {q.considered}
            </p>
          )}
          {q.comment && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">Şərh: </span>
              {q.comment}
            </p>
          )}
          {q.sources.length > 0 && (
            <p className="text-gray-700 dark:text-gray-200 break-all">
              <span className="font-bold text-gray-900 dark:text-gray-50">Mənbə: </span>
              {q.sources.map((src, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <><br />&nbsp;&nbsp;&nbsp;&nbsp;</>}
                  {q.sources.length > 1 ? `${i + 1}. ${src}` : src}
                </React.Fragment>
              ))}
            </p>
          )}
          {q.authors.length > 0 && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">
                {q.authors.length > 1 ? "Müəlliflər:" : "Müəllif:"}
              </span>{" "}
              {q.authors.map((a, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? ", " : ""}
                  {a.fullname}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default QuestionView;
